import type {
	AgentEvent,
	AttachedFile,
	ProjectProposal,
	ReviewRequestedPayload,
	ReconciliationQuestion,
	StreamCallbacks,
} from './types';
import {SimpleUUID, AGENT_URL} from './constants';

enum StateDeltaType {
	RefiningStarted = 'refining_started',
	Proposal = 'proposal',
	Warnings = 'warnings',
	Committed = 'committed',
	ClassificationBlocked = 'classification_blocked',
	PlaceholderProgress = 'placeholder_progress',
}

/**
 * The sentence the gateway wrote for a refusal it made before calling the
 * model, or null for any other failure.
 *
 * Two refusals today: the account's AI allowance is spent (402), and gen AI is
 * switched off for the account or the whole platform (403). Neither is a fault
 * and neither must read like one -- surfacing either through onError would
 * style it as a failure and prefix it with a status code, which is what sends
 * people to support over something that is working as intended.
 *
 * A 503 is deliberately not matched: it means the gateway could not confirm the
 * state, which is a retryable outage and belongs in the error path.
 */
export function gatewayRefusalMessage(status: number, body: string): string | null {
	try {
		const parsed = JSON.parse(body) as {error?: {code?: string; message?: string}};
		const code = parsed?.error?.code;
		const matches = (status === 402 && code === 'BUDGET_EXHAUSTED') || (status === 403 && code === 'GEN_AI_DISABLED');
		if (!matches) return null;
		const message = parsed.error?.message;
		return typeof message === 'string' && message.trim().length > 0 ? message.trim() : null;
	} catch {
		return null;
	}
}

/**
 * Deliver a refusal as an ordinary agent message.
 *
 * Streams it through the same onText / onMessageStreamEnd pair a real reply
 * uses, so the modal renders it where its answers appear rather than in an
 * error state.
 */
function deliverRefusal(callbacks: StreamCallbacks, message: string): void {
	// The modal has to leave its "reading your SoW" screen as well as show the
	// text: a refusal arrives before any stream starts, so nothing else will
	// move it off that screen and the spinner would run forever. onText alone
	// puts the message on a panel the user cannot see yet.
	if (callbacks.onBudgetExhausted) {
		callbacks.onBudgetExhausted(message);
		return;
	}
	const messageId = SimpleUUID();
	callbacks.onText(messageId, message);
	callbacks.onMessageStreamEnd(messageId);
}

export async function parseSSEStream(response: Response, callbacks: StreamCallbacks, signal?: AbortSignal): Promise<void> {
	if (!response.body) return;
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let pendingEventType: string | null = null;

	// Cancel the reader immediately when the signal fires so that a blocked
	// reader.read() (e.g. waiting for the next SSE chunk) unblocks right away.
	const abortHandler = () => {
		reader.cancel().catch(() => undefined);
	};
	signal?.addEventListener('abort', abortHandler);

	try {
		for (;;) {
			if (signal?.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}
			const {done, value} = await reader.read();
			if (done) {
				if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
				break;
			}

			buffer += decoder.decode(value, {stream: true});
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';

			for (const rawLine of lines) {
				const line = rawLine.trimEnd();

				if (line.startsWith('event: ')) {
					pendingEventType = line.slice(7).trim();
					continue;
				}

				if (line.startsWith('data: ')) {
					const dataStr = line.slice(6).trim();
					let parsed: AgentEvent;
					try {
						parsed = JSON.parse(dataStr) as AgentEvent;
					} catch {
						if (pendingEventType === 'REVIEW_REQUESTED' || pendingEventType === 'RECONCILIATION_QUESTION') {
							callbacks.onError('A processing error occurred. Please try again.');
						}
						pendingEventType = null;
						continue;
					}

					// Custom STATE_DELTA event
					if (pendingEventType === 'STATE_DELTA') {
						if (parsed.type === StateDeltaType.RefiningStarted) {
							callbacks.onRefiningStarted();
						} else if (parsed.type === StateDeltaType.Proposal) {
							callbacks.onProposal(parsed.proposal ?? (parsed as unknown as ProjectProposal));
						} else if (parsed.type === StateDeltaType.Warnings) {
							callbacks.onWarnings(parsed.warnings ?? []);
						} else if (parsed.type === StateDeltaType.Committed) {
							callbacks.onCommitted(
								parsed.project_id ?? null,
								parsed.relay_project_id,
								parsed.deliverable_count,
								parsed.role_count,
								parsed.baseline_notices
							);
						} else if (parsed.type === StateDeltaType.ClassificationBlocked) {
							callbacks.onClassificationBlocked?.(parsed.block_reason);
						} else if (parsed.type === StateDeltaType.PlaceholderProgress && parsed.placeholders) {
							callbacks.onPlaceholderProgress?.(parsed.placeholders);
						}
						pendingEventType = null;
						continue;
					}

					if (pendingEventType === 'REVIEW_REQUESTED') {
						const payload = parsed as unknown as ReviewRequestedPayload;
						if (payload.proposal) {
							callbacks.onReviewRequested?.(payload);
						} else {
							// Backend sent REVIEW_REQUESTED without a proposal — surface as an error
							// so the user sees a message rather than being stuck on the checklist
							callbacks.onError(
								'The project could not be extracted from the document. Please try again or switch to manual setup.'
							);
						}
						pendingEventType = null;
						continue;
					}

					if (pendingEventType === 'RECONCILIATION_QUESTION') {
						callbacks.onReconciliationQuestion?.(parsed as unknown as ReconciliationQuestion);
						pendingEventType = null;
						continue;
					}

					pendingEventType = null;

					// Standard AG-UI events
					// ag_ui serializes with by_alias=True + alias_generator=to_camel → camelCase field names
					const eventType: string = parsed.type ?? '';

					switch (eventType) {
						case 'TEXT_MESSAGE_START': {
							const id = parsed.messageId ?? SimpleUUID();
							callbacks.onNewAssistantMessage(id);
							break;
						}
						case 'TEXT_MESSAGE_CONTENT': {
							const id = parsed.messageId ?? '';
							callbacks.onText(id, parsed.delta ?? '');
							break;
						}
						case 'TEXT_MESSAGE_END': {
							callbacks.onMessageStreamEnd(parsed.messageId ?? '');
							break;
						}
						case 'TOOL_CALL_START': {
							const toolCallId = parsed.toolCallId ?? SimpleUUID();
							const toolName = parsed.toolCallName ?? 'tool';
							callbacks.onToolCall(toolCallId, toolName);
							break;
						}
						case 'TOOL_CALL_RESULT': {
							callbacks.onToolResult(parsed.toolCallId ?? '');
							break;
						}
						case 'RUN_FINISHED': {
							callbacks.onFinished();
							break;
						}
						case 'RUN_ERROR': {
							callbacks.onError(parsed.message ?? 'An unknown error occurred');
							break;
						}
						case 'STEP_STARTED': {
							callbacks.onStepStarted?.(parsed.stepName ?? parsed.step ?? '');
							break;
						}
						case 'STEP_FINISHED': {
							callbacks.onStepFinished?.(parsed.stepName ?? parsed.step ?? '');
							break;
						}
						default:
							break;
					}
				}
			}
		}
	} finally {
		signal?.removeEventListener('abort', abortHandler);
		reader.releaseLock();
	}
}

export async function invokeAgent(
	userText: string,
	threadId: string,
	apiKey: string,
	callbacks: StreamCallbacks,
	signal: AbortSignal,
	attachment?: AttachedFile,
	personId?: number,
	companyId?: string,
	companyName?: string
): Promise<void> {
	const runId = SimpleUUID();
	const msgId = SimpleUUID();

	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (apiKey) headers['x-forecast-api-key'] = apiKey;
	if (personId != null) headers['x-person-id'] = String(personId);
	// Tenant headers for telemetry. Omit entirely when unavailable —
	// the gateway treats absent and empty headers differently.
	if (companyId) headers['X-Forecast-Company-Id'] = companyId;
	if (companyName) headers['X-Forecast-Company-Name'] = companyName;
	headers['x-agent-id'] = 'project_planning';
	// Forwarded through the gateway to every MCP tool call this run makes, including
	// create_draft_invoice's created-date/due-date stamping (see useAIChatStream.ts for the
	// same pattern) — without it, the MCP server falls back to UTC, which stamps the wrong
	// calendar day for any user whose local morning is still "yesterday" in UTC.
	const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (tz) headers['X-User-Timezone'] = tz;

	const response = await fetch(`${AGENT_URL}/invocations`, {
		method: 'POST',
		headers,
		credentials: 'include',
		signal,
		body: JSON.stringify({
			api_key: apiKey,
			thread_id: threadId,
			run_id: runId,
			state: {},
			messages: [{id: msgId, role: 'user', content: userText}],
			tools: [],
			context: [],
			forwardedProps: {agent_id: 'project_planning'},
			...(attachment && {
				attachments: [
					{
						filename: attachment.filename,
						mime_type: attachment.mimeType,
						data: attachment.dataBase64,
					},
				],
			}),
		}),
	});

	if (!response.ok) {
		const text = await response.text().catch(() => '');
		const refusal = gatewayRefusalMessage(response.status, text);
		if (refusal) {
			deliverRefusal(callbacks, refusal);
			return;
		}
		callbacks.onError(`Server error ${response.status}: ${text.slice(0, 200)}`);
		return;
	}

	await parseSSEStream(response, callbacks, signal);
}

export async function resumeAgent(
	threadId: string,
	approved: boolean,
	apiKey: string,
	callbacks: StreamCallbacks,
	signal: AbortSignal,
	refinement?: string,
	personId?: number,
	proposal?: ProjectProposal | null,
	companyId?: string,
	companyName?: string,
	// reconciliationAnswer: for RECONCILIATION_QUESTION interrupts (reconcile_sow reads `state.answer`)
	reconciliationAnswer?: string,
	createPlaceholdersEnabled?: boolean,
	invoicesEnabled?: boolean,
	// The rate card the viewer picked on the review form, sent as rate_card_id. null means no rate
	// card; undefined (the legacy review form, which has no Rate Card field) omits the key.
	rateCardId?: string | null
): Promise<void> {
	const runId = SimpleUUID();
	const msgId = SimpleUUID();

	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (apiKey) headers['x-forecast-api-key'] = apiKey;
	if (personId != null) headers['x-person-id'] = String(personId);
	// Tenant headers for telemetry. Omit entirely when unavailable —
	// the gateway treats absent and empty headers differently.
	if (companyId) headers['X-Forecast-Company-Id'] = companyId;
	if (companyName) headers['X-Forecast-Company-Name'] = companyName;
	headers['x-agent-id'] = 'project_planning';
	// Forwarded through the gateway to every MCP tool call this run makes, including
	// create_draft_invoice's created-date/due-date stamping (see useAIChatStream.ts for the
	// same pattern) — without it, the MCP server falls back to UTC, which stamps the wrong
	// calendar day for any user whose local morning is still "yesterday" in UTC.
	const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (tz) headers['X-User-Timezone'] = tz;

	// state becomes the interrupt resume value — backend routing:
	//   reconciliationAnswer → {answer} read by reconcile_sow interrupt
	//   refinement           → {approved:false, refinement} read by await_approval
	//   else                 → {approved, proposal?, create_placeholders_enabled?, invoices_enabled?, rate_card_id?} read by await_approval
	const state = reconciliationAnswer
		? {answer: reconciliationAnswer}
		: refinement
		? {approved: false, refinement}
		: {
				approved,
				...(proposal ? {proposal} : {}),
				...(createPlaceholdersEnabled !== undefined ? {create_placeholders_enabled: createPlaceholdersEnabled} : {}),
				...(invoicesEnabled !== undefined ? {invoices_enabled: invoicesEnabled} : {}),
				...(rateCardId !== undefined ? {rate_card_id: rateCardId} : {}),
		  };
	const userContent = reconciliationAnswer ?? refinement ?? (approved ? 'Approved' : 'Cancelled');

	const response = await fetch(`${AGENT_URL}/invocations`, {
		method: 'POST',
		headers,
		credentials: 'include',
		signal,
		body: JSON.stringify({
			api_key: apiKey,
			thread_id: threadId,
			run_id: runId,
			state,
			messages: [{id: msgId, role: 'user', content: userContent}],
			tools: [],
			context: [],
			forwardedProps: {agent_id: 'project_planning'},
		}),
	});

	if (!response.ok) {
		const text = await response.text().catch(() => '');
		const refusal = gatewayRefusalMessage(response.status, text);
		if (refusal) {
			deliverRefusal(callbacks, refusal);
			return;
		}
		callbacks.onError(`Server error ${response.status}: ${text.slice(0, 200)}`);
		return;
	}

	await parseSSEStream(response, callbacks, signal);
}
